import React, { useMemo } from 'react';
import { SparkTagDimensionSelection } from '../../types/sparkTypes';
import tagStyles from '../../styles/TagContextSelector.module.css';
import cardStyles from '../../styles/CreativeCard.module.css';
import listStyles from '../../styles/CreativeListItem.module.css';
import ImageIcon from '@mui/icons-material/Image';
import { useAtlasStore } from '../../../../store/atlasStore';

export const CreativeListItem: React.FC<{
  fqn: string;
  title: string;
  description: string;
  thumbnailUrl?: string;
  tagPattern: SparkTagDimensionSelection[] | null;
  labels?: string[];
  publishedAt?: string | null;
  publishedBy?: string | null;
  state?: 'draft' | 'published';
  isPublished?: boolean;
  onClick: () => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelected?: (selected: boolean) => void;
}> = ({ fqn, title, description, thumbnailUrl, tagPattern, labels, publishedAt, publishedBy, state, isPublished, onClick, selectable = false, selected = false, onToggleSelected }) => {
  const elementNameMap = useMemo(() => {
    const dims = useAtlasStore.getState().dimensions || [];
    const map = new Map<string, string>();
    dims.forEach(d => (d.elements || []).forEach(e => { if (e?.fqn && e?.name) map.set(e.fqn, e.name); }));
    return map;
  }, []);

  const elementNames = useMemo(() => {
    const names: string[] = [];
    (tagPattern || []).forEach(entry => (entry.element_fqns || []).forEach(fqn => {
      const name = elementNameMap.get(fqn);
      if (name) names.push(name);
    }));
    return names;
  }, [tagPattern, elementNameMap]);

  const pubDate = useMemo(() => {
    if (!publishedAt) return '';
    const d = new Date(publishedAt);
    try {
      // Use browser locale with a readable default style
      return d.toLocaleDateString(undefined, { dateStyle: 'medium' as const });
    } catch {
      return d.toLocaleDateString();
    }
  }, [publishedAt]);

  return (
    <div
      onClick={onClick}
      className={`${listStyles.row} ${selected ? listStyles.selectedRow : ''}`}
      style={{ display: 'flex', gap: 12, border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, cursor: 'pointer', background: '#fff' }}
    >
      {selectable && (
        <div className={listStyles.selectionOverlay} onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className={listStyles.selectionCheckbox}
            checked={selected}
            onChange={(e) => onToggleSelected && onToggleSelected(e.target.checked)}
            aria-label={selected ? 'Deselect' : 'Select'}
          />
        </div>
      )}
      <div style={{ width: 140, height: 100, flexShrink: 0, background: '#f3f4f6', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div className={cardStyles.thumbnailPlaceholder} style={{ position: 'relative' }}>
            <ImageIcon fontSize="large" />
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 100, position: 'relative' }}>
        {pubDate && (
          <span className={cardStyles.typeBadge} style={{ position: 'absolute', top: 0, right: 0 }}>{pubDate}</span>
        )}
        <div className={listStyles.listName}>{title}</div>
        {description && (
          <div className={listStyles.listDescription} title={description || ''}>
            {description}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto' }}>
          {/* context tag badges - left */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {elementNames.slice(0, 6).map((name, idx) => (
              <span key={`${name}-${idx}`} className={`${tagStyles.headerChip} ${listStyles.listTagChip}`}>
                <span className={tagStyles.headerChipLabel}>{name}</span>
              </span>
            ))}
            {elementNames.length > 6 && (
              <span className={`${tagStyles.headerChip} ${listStyles.listTagChip}`}>+{elementNames.length - 6}</span>
            )}
          </div>
          {/* labels + publication badges - right */}
          <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {(labels || []).map(label => (
              <span key={label} className={cardStyles.labelPill}>
                <span className={cardStyles.labelPillText}>{label}</span>
              </span>
            ))}
            {isPublished && (
              <span className={cardStyles.publishedBadge}>Published</span>
            )}
            {state === 'draft' && (
              <span className={cardStyles.draftBadge}>Draft</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
